import { ref, provide, inject, type Ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { usePrevioCore } from '@previo/core'

const DATE_TIME_PICKER_KEY = Symbol('DateTimePickerState')

export interface DateTimePickerState {
  date: Ref<Dayjs | null>
  currentView: Ref<Dayjs>
  currentCalendar: () => Dayjs
  secondCalendar: () => Dayjs
  subtractMonth: () => void
  advanceMonth: () => void
  currentMonthFormatted: () => string
  nextMonthFormatted: () => string
  setDate: (newDate: Dayjs) => void
  getDate: () => Dayjs | null
  clearDate: () => void
  setMin: (date: Dayjs) => void
  setMax: (date: Dayjs) => void
  setDisableClearable: (disable: boolean) => void
  navigateToCurrentDate: (date: Dayjs) => void
  isDisableClearable: () => boolean
  getMin: () => Dayjs | null
  getMax: () => Dayjs | null
}

const createDateTimePickerState = (): DateTimePickerState => {
  const core = usePrevioCore()

  const date = ref<Dayjs | null>(null)
  const min = ref<Dayjs | null>(null)
  const max = ref<Dayjs | null>(null)
  const disableClearable = ref<boolean>(false)
  const currentView = ref<Dayjs>(dayjs())

  const currentCalendar = (): Dayjs => {
    return currentView.value
  }

  const secondCalendar = (): Dayjs => {
    return currentView.value.add(1, 'month')
  }

  const subtractMonth = (): void => {
    currentView.value = currentView.value.subtract(1, 'month')
  }

  const advanceMonth = (): void => {
    currentView.value = currentView.value.add(1, 'month')
  }

  const navigateToCurrentDate = (date: Dayjs): void => {
    currentView.value = date
  }

  const currentMonthFormatted = (): string => {
    const month = currentView.value.locale(core.useLocale().get()).format('MMMM')
    return month.charAt(0).toUpperCase() + month.slice(1)
  }

  const nextMonthFormatted = (): string => {
    const month = currentView.value.locale(core.useLocale().get()).add(1, 'month').format('MMMM')
    return month.charAt(0).toUpperCase() + month.slice(1)
  }

  const getDate = (): Dayjs | null => {
    return date.value
  }

  const setDate = (newDate: Dayjs): void => {
    date.value = newDate
    currentView.value = newDate
  }

  const getMin = (): Dayjs | null => {
    return min.value
  }

  const setMin = (date: Dayjs): void => {
    min.value = date
  }

  const getMax = (): Dayjs | null => {
    return max.value
  }

  const setMax = (date: Dayjs): void => {
    max.value = date
  }

  const setDisableClearable = (disable: boolean): void => {
    disableClearable.value = disable
  }

  const isDisableClearable = (): boolean => {
    return disableClearable.value
  }

  const clearDate = (): void => {
    date.value = null
  }

  return {
    date,
    currentView,
    currentCalendar,
    secondCalendar,
    subtractMonth,
    advanceMonth,
    navigateToCurrentDate,
    currentMonthFormatted,
    nextMonthFormatted,
    setDate,
    getDate,
    clearDate,
    setMin,
    setMax,
    getMin,
    getMax,
    setDisableClearable,
    isDisableClearable,
  }
}

export const provideDateTimePickerState = (): DateTimePickerState => {
  const navigationState = createDateTimePickerState()

  provide(DATE_TIME_PICKER_KEY, navigationState)

  return navigationState
}

export const useDateTimePicker = (): DateTimePickerState => {
  const navigationState: DateTimePickerState | undefined = inject(DATE_TIME_PICKER_KEY)

  if (!navigationState) {
    throw new Error('useDateTimePicker() must be used within a provideDateTimePickerState() scope')
  }

  return navigationState
}
